/*
  menara-collections.js — page « Les Collections ».

  Un seul état : l'univers actif. Le bandeau, le sélecteur, la présentation
  et le bandeau « Prochain univers » le suivent ensemble, quelle que soit la
  commande utilisée : vignette, flèche, balayage, clavier ou bandeau suivant.

  Les grandes ambiances ne sont chargées qu'au moment d'être montrées (et la
  suivante probable juste après). Les mouvements n'écrivent que transform et
  opacity ; la profondeur au curseur ne tourne qu'au survol, sur ordinateur.
*/

const MOUVEMENT_REDUIT = window.matchMedia('(prefers-reduced-motion: reduce)');
const POINTEUR_FIN = window.matchMedia('(hover: hover) and (pointer: fine)');

const modulo = (a, n) => ((a % n) + n) % n;
const borne = (v, min, max) => Math.min(max, Math.max(min, v));

class MenaraCollections extends HTMLElement {
  connectedCallback() {
    if (this.moteur) return;
    this.moteur = true;
    this.demarrer();
  }

  disconnectedCallback() {
    this.arreter();
    this.moteur = false;
  }

  get calme() {
    return this.dataset.animations === 'false' || MOUVEMENT_REDUIT.matches;
  }

  ecouter(el, type, fn, options) {
    if (!el) return;
    el.addEventListener(type, fn, options);
    this.nettoyeurs.push(() => el.removeEventListener(type, fn, options));
  }

  plusTard(fn, ms) {
    const t = setTimeout(() => {
      this.minuteurs.delete(t);
      fn();
    }, ms);
    this.minuteurs.add(t);
    return t;
  }

  demarrer() {
    this.nettoyeurs = [];
    this.minuteurs = new Set();
    this.diapos = Array.from(this.querySelectorAll('[data-mcol-diapo]'));
    this.onglets = Array.from(this.querySelectorAll('[data-mcol-onglet]'));
    this.panneaux = Array.from(this.querySelectorAll('[data-mcol-panneau]'));
    this.suivants = Array.from(this.querySelectorAll('[data-mcol-suivant]'));
    this.n = this.onglets.length;
    if (!this.n) return;

    this.design = Boolean(window.Shopify && window.Shopify.designMode);
    this.intro = this.querySelector('[data-mcol-intro]');
    this.choix = this.querySelector('[data-mcol-choix]');
    this.zonePanneaux = this.querySelector('[data-mcol-panneaux]');
    this.numero = this.querySelector('[data-mcol-num]');
    this.annonce = this.querySelector('[data-mcol-annonce]');
    this.intensite = parseFloat(getComputedStyle(this).getPropertyValue('--mcol-i')) || 0.6;

    // Un lien partagé « #univers-medinas » ouvre directement cet univers.
    let depart = borne(parseInt(this.dataset.depart, 10) || 0, 0, this.n - 1);
    const hash = window.location.hash || '';
    if (hash.startsWith('#univers-')) {
      const cle = decodeURIComponent(hash.slice(9));
      const i = this.onglets.findIndex((o) => o.dataset.cle === cle);
      if (i >= 0) depart = i;
    }

    this.actif = -1;
    this.classList.add('mcol--pret');
    if (this.calme || this.design) this.classList.add('mcol--calme');

    this.ameliorerAccessibilite();
    this.appliquer(depart, { immediat: true });

    this.brancherCommandes();
    this.brancherRails();
    this.brancherApparitions();
    this.brancherParallaxe();
    this.brancherBalayage();
    this.brancherVisibilite();
    this.brancherEditeur();
  }

  arreter() {
    (this.nettoyeurs || []).forEach((f) => f());
    this.nettoyeurs = [];
    (this.minuteurs || new Set()).forEach((t) => clearTimeout(t));
    if (this.minuteurs) this.minuteurs.clear();
    clearTimeout(this.minuteurPanneau);
    clearTimeout(this.minuteurAuto);
    if (this.parallaxe) cancelAnimationFrame(this.parallaxe.raf);
  }

  /* — Accessibilité : les vignettes deviennent des onglets — */

  ameliorerAccessibilite() {
    const rail = this.querySelector('[data-mcol-onglets]');
    if (rail) {
      rail.setAttribute('role', 'tablist');
      rail.setAttribute('aria-label', 'Univers');
    }
    this.onglets.forEach((o, i) => {
      const p = this.panneaux[i];
      o.setAttribute('role', 'tab');
      if (p) {
        o.setAttribute('aria-controls', p.id);
        p.setAttribute('role', 'tabpanel');
        p.setAttribute('aria-labelledby', o.id);
      }
    });
  }

  /* — L'état central — */

  appliquer(i, options = {}) {
    const cible = modulo(i, this.n);
    const precedent = this.actif;
    if (cible === precedent && !options.immediat) return;

    const avance = precedent < 0 ? 1 : modulo(cible - precedent, this.n) <= this.n / 2 ? 1 : -1;
    const immediat = Boolean(options.immediat) || this.calme;
    this.actif = cible;

    this.majDiapos(cible, precedent, avance, immediat);

    this.onglets.forEach((o, k) => {
      const est = k === cible;
      o.classList.toggle('est-active', est);
      o.setAttribute('aria-selected', est ? 'true' : 'false');
      o.tabIndex = est ? 0 : -1;
    });
    this.centrerOnglet(this.onglets[cible], immediat);

    this.majPanneaux(cible, precedent, immediat);

    this.suivants.forEach((s, k) => {
      s.hidden = k !== cible;
      if (k === cible && precedent >= 0) s.classList.add('est-visible');
    });

    if (this.numero) this.numero.textContent = String(cible + 1).padStart(2, '0');

    if (precedent >= 0 && !this.design) {
      try {
        history.replaceState(history.state, '', `#univers-${this.onglets[cible].dataset.cle}`);
      } catch (_) {
        /* navigation privée stricte : l'adresse reste inchangée */
      }
    }

    if (options.annoncer && this.annonce) {
      const nom = this.onglets[cible].querySelector('[data-mcol-nom]');
      this.annonce.textContent = `Univers ${nom ? nom.textContent.trim() : ''}, ${cible + 1} sur ${this.n}`;
    }
  }

  charger(d) {
    if (!d || d.dataset.charge === '1') return;
    d.querySelectorAll('source[data-srcset]').forEach((s) => {
      s.srcset = s.dataset.srcset;
      s.removeAttribute('data-srcset');
    });
    d.querySelectorAll('img[data-srcset]').forEach((img) => {
      img.srcset = img.dataset.srcset;
      img.removeAttribute('data-srcset');
      if (img.dataset.src) {
        img.src = img.dataset.src;
        img.removeAttribute('data-src');
      }
    });
    d.dataset.charge = '1';
  }

  majDiapos(cible, precedent, avance, immediat) {
    const entrante = this.diapos[cible];
    if (!entrante) return;
    this.charger(entrante);

    const activer = () => {
      if (this.actif !== cible) return;
      if (!immediat) {
        // Position de départ posée sans transition, puis le fondu démarre.
        entrante.classList.add('mcol__diapo--prepare');
        entrante.style.setProperty('--mcol-sens', String(avance));
        void entrante.offsetWidth;
        entrante.classList.remove('mcol__diapo--prepare');
      }
      this.diapos.forEach((d, k) => {
        const est = k === cible;
        if (!est && d.classList.contains('est-active')) d.style.setProperty('--mcol-sens', String(-avance));
        d.classList.toggle('est-active', est);
        d.setAttribute('aria-hidden', est ? 'false' : 'true');
        if (!est) this.remettrePlan(d);
      });
      this.plusTard(() => this.charger(this.diapos[modulo(cible + avance, this.n)]), 900);
    };

    const img = entrante.querySelector('img');
    if (immediat || !img || img.complete) {
      activer();
      return;
    }
    // On attend l'image, un court instant au plus, pour ne jamais fondre vers du vide.
    let fait = false;
    const une = () => {
      if (fait) return;
      fait = true;
      activer();
    };
    (img.decode ? img.decode() : Promise.resolve()).then(une, une);
    this.plusTard(une, 500);
  }

  majPanneaux(cible, precedent, immediat) {
    const entrant = this.panneaux[cible];
    if (!entrant) return;
    clearTimeout(this.minuteurPanneau);

    const montrer = () => {
      this.panneaux.forEach((p, k) => {
        if (k === cible) return;
        p.hidden = true;
        p.classList.remove('est-sortant', 'est-entrant');
      });
      entrant.hidden = false;
      entrant.classList.remove('est-sortant');
      if (precedent >= 0) {
        entrant.querySelectorAll('[data-mcol-apparition]').forEach((el) => el.classList.add('est-visible'));
      }
      this.majRail(entrant.querySelector('[data-mcol-rail]'));
      if (!immediat) {
        entrant.classList.remove('est-entrant');
        void entrant.offsetWidth;
        entrant.classList.add('est-entrant');
      }
      if (this.zonePanneaux) requestAnimationFrame(() => { this.zonePanneaux.style.minHeight = ''; });
    };

    const sortant = precedent >= 0 ? this.panneaux[precedent] : null;
    if (immediat || !sortant || sortant.hidden) {
      montrer();
      return;
    }
    // Hauteur tenue pendant l'échange : rien ne saute sous la présentation.
    if (this.zonePanneaux) this.zonePanneaux.style.minHeight = `${this.zonePanneaux.offsetHeight}px`;
    sortant.classList.add('est-sortant');
    this.minuteurPanneau = setTimeout(montrer, 190);
  }

  centrerOnglet(o, immediat) {
    const rail = o && o.parentElement;
    if (!rail || rail.scrollWidth <= rail.clientWidth + 1) return;
    const gauche = Math.max(0, o.offsetLeft - (rail.clientWidth - o.offsetWidth) / 2);
    try {
      rail.scrollTo({ left: gauche, behavior: immediat ? 'instant' : 'smooth' });
    } catch (_) {
      rail.scrollLeft = gauche;
    }
  }

  // « instant » et non « auto » : le thème peut imposer un défilement doux en
  // CSS, que l'on ne veut pas quand les animations sont réduites.
  get comportement() {
    return this.calme ? 'instant' : 'smooth';
  }

  defiler(el) {
    if (!el) return;
    try {
      el.scrollIntoView({ behavior: this.comportement, block: 'start' });
    } catch (_) {
      el.scrollIntoView(true);
    }
  }

  interaction() {
    this.pause = Date.now() + 12000;
    this.planifierAuto();
  }

  /* — Commandes — */

  brancherCommandes() {
    const modifie = (e) => e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button > 0;

    this.onglets.forEach((o, i) => {
      this.ecouter(o, 'click', (e) => {
        if (modifie(e)) return;
        e.preventDefault();
        this.interaction();
        this.appliquer(i);
      });
      this.ecouter(o, 'keydown', (e) => {
        let j = null;
        if (e.key === 'ArrowRight') j = i + 1;
        else if (e.key === 'ArrowLeft') j = i - 1;
        else if (e.key === 'Home') j = 0;
        else if (e.key === 'End') j = this.n - 1;
        if (j === null) return;
        e.preventDefault();
        j = modulo(j, this.n);
        this.interaction();
        this.appliquer(j);
        this.onglets[j].focus({ preventScroll: true });
      });
    });

    this.ecouter(this.querySelector('[data-mcol-prec]'), 'click', () => {
      this.interaction();
      this.appliquer(this.actif - 1, { annoncer: true });
    });
    this.ecouter(this.querySelector('[data-mcol-suiv]'), 'click', () => {
      this.interaction();
      this.appliquer(this.actif + 1, { annoncer: true });
    });

    this.ecouter(this.querySelector('[data-mcol-explorer]'), 'click', (e) => {
      if (!this.choix || modifie(e)) return;
      e.preventDefault();
      this.defiler(this.choix);
      // Au clavier, le focus suit : on arrive sur l'univers actif.
      if (e.detail === 0) this.onglets[this.actif].focus({ preventScroll: true });
    });

    this.suivants.forEach((s) => {
      this.ecouter(s, 'click', (e) => {
        if (modifie(e)) return;
        e.preventDefault();
        this.interaction();
        this.appliquer(this.actif + 1, { annoncer: true });
        this.defiler(this.choix);
        if (e.detail === 0) this.onglets[this.actif].focus({ preventScroll: true });
      });
    });
  }

  /* — Rails de pièces — */

  brancherRails() {
    this.rails = Array.from(this.querySelectorAll('[data-mcol-rail]'));
    this.rails.forEach((rail) => {
      const zone = rail.closest('[data-mcol-rail-zone]');
      if (!zone) return;
      const pas = () => {
        const carte = rail.querySelector('[data-mcol-carte]');
        if (!carte) return rail.clientWidth;
        return carte.offsetWidth + (parseFloat(getComputedStyle(rail).columnGap) || 0);
      };
      const bouger = (sens) => (e) => {
        if (e.currentTarget.getAttribute('aria-disabled') === 'true') return;
        try {
          rail.scrollBy({ left: sens * pas(), behavior: this.comportement });
        } catch (_) {
          rail.scrollLeft += sens * pas();
        }
        // État des flèches recalculé à l'arrivée, même si aucun événement
        // de défilement n'est émis.
        this.plusTard(() => this.majRail(rail), this.calme ? 0 : 600);
      };
      this.ecouter(zone.querySelector('[data-mcol-rail-prec]'), 'click', bouger(-1));
      this.ecouter(zone.querySelector('[data-mcol-rail-suiv]'), 'click', bouger(1));
      // Mise à jour groupée des boutons, sans dépendre des images d'animation.
      let attente = 0;
      this.ecouter(rail, 'scroll', () => {
        if (attente) return;
        attente = setTimeout(() => {
          attente = 0;
          this.majRail(rail);
        }, 80);
      }, { passive: true });
      this.nettoyeurs.push(() => clearTimeout(attente));
    });

    if ('ResizeObserver' in window) {
      const ro = new ResizeObserver((entrees) => entrees.forEach((e) => this.majRail(e.target)));
      this.rails.forEach((r) => ro.observe(r));
      this.nettoyeurs.push(() => ro.disconnect());
    }
    this.rails.forEach((r) => this.majRail(r));
  }

  majRail(rail) {
    if (!rail) return;
    const zone = rail.closest('[data-mcol-rail-zone]');
    if (!zone) return;
    const max = rail.scrollWidth - rail.clientWidth;
    const deborde = max > 2;
    zone.classList.toggle('mcol__rail-zone--defile', deborde);
    const prec = zone.querySelector('[data-mcol-rail-prec]');
    const suiv = zone.querySelector('[data-mcol-rail-suiv]');
    if (prec) prec.setAttribute('aria-disabled', !deborde || rail.scrollLeft <= 2 ? 'true' : 'false');
    if (suiv) suiv.setAttribute('aria-disabled', !deborde || rail.scrollLeft >= max - 2 ? 'true' : 'false');
  }

  /* — Apparition à l'entrée dans l'écran — */

  brancherApparitions() {
    const elements = Array.from(this.querySelectorAll('[data-mcol-apparition]'));
    if (this.calme || this.design || !('IntersectionObserver' in window)) {
      elements.forEach((el) => el.classList.add('est-visible'));
      return;
    }
    const io = new IntersectionObserver((entrees) => {
      entrees.forEach((e) => {
        if (!e.isIntersecting) return;
        e.target.classList.add('est-visible');
        io.unobserve(e.target);
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -6% 0px' });
    elements.forEach((el) => io.observe(el));
    this.nettoyeurs.push(() => io.disconnect());
  }

  /* — Profondeur au curseur : ordinateur uniquement — */

  brancherParallaxe() {
    if (!this.intro || this.calme || this.design || !POINTEUR_FIN.matches || this.intensite <= 0) return;
    const px = (this.parallaxe = { x: 0, y: 0, cx: 0, cy: 0, raf: 0 });
    const force = this.intensite;

    const pas = () => {
      px.raf = 0;
      px.x += (px.cx - px.x) * 0.08;
      px.y += (px.cy - px.y) * 0.08;
      const diapo = this.diapos[this.actif];
      const plan = diapo && diapo.querySelector('[data-mcol-plan]');
      if (plan) {
        plan.style.transform = `translate3d(${(px.x * -14 * force).toFixed(2)}px,${(px.y * -9 * force).toFixed(2)}px,0) scale(${(1 + 0.035 * force).toFixed(4)})`;
      }
      if (Math.abs(px.cx - px.x) > 0.002 || Math.abs(px.cy - px.y) > 0.002) px.raf = requestAnimationFrame(pas);
    };
    const relancer = () => {
      if (!px.raf) px.raf = requestAnimationFrame(pas);
    };

    // Aucune mesure : la position rapportée à la fenêtre suffit.
    this.ecouter(this.intro, 'pointermove', (e) => {
      if (e.pointerType !== 'mouse') return;
      px.cx = (e.clientX / window.innerWidth) * 2 - 1;
      px.cy = (e.clientY / window.innerHeight) * 2 - 1;
      relancer();
    }, { passive: true });
    this.ecouter(this.intro, 'pointerleave', () => {
      px.cx = 0;
      px.cy = 0;
      relancer();
    });
  }

  remettrePlan(d) {
    const plan = d.querySelector('[data-mcol-plan]');
    if (plan) plan.style.transform = '';
  }

  /* — Balayage tactile du bandeau — */

  brancherBalayage() {
    if (!this.intro || this.n < 2) return;
    let depart = null;
    this.ecouter(this.intro, 'pointerdown', (e) => {
      if (e.pointerType === 'mouse' || e.target.closest('a, button')) return;
      depart = { x: e.clientX, y: e.clientY, id: e.pointerId };
    }, { passive: true });
    this.ecouter(this.intro, 'pointerup', (e) => {
      if (!depart || e.pointerId !== depart.id) return;
      const dx = e.clientX - depart.x;
      const dy = e.clientY - depart.y;
      depart = null;
      if (Math.abs(dx) < 48 || Math.abs(dx) < Math.abs(dy) * 1.2) return;
      this.interaction();
      this.appliquer(this.actif + (dx < 0 ? 1 : -1), { annoncer: true });
    });
    this.ecouter(this.intro, 'pointercancel', () => { depart = null; });
  }

  /* — Défilement automatique, désactivé par défaut — */

  brancherVisibilite() {
    this.auto = this.dataset.auto === 'true' && !this.design && !this.calme && this.n > 1;
    if (!this.auto || !this.intro) return;
    const io = new IntersectionObserver(([e]) => {
      this.visible = e.isIntersecting;
      if (this.visible) this.planifierAuto();
      else clearTimeout(this.minuteurAuto);
    }, { threshold: 0.3 });
    io.observe(this.intro);
    this.nettoyeurs.push(() => io.disconnect());
    this.ecouter(this.intro, 'pointerenter', () => { this.survol = true; clearTimeout(this.minuteurAuto); });
    this.ecouter(this.intro, 'pointerleave', () => { this.survol = false; this.planifierAuto(); });
    this.ecouter(this, 'focusin', () => { this.focusDedans = true; clearTimeout(this.minuteurAuto); });
    this.ecouter(this, 'focusout', (e) => {
      if (this.contains(e.relatedTarget)) return;
      this.focusDedans = false;
      this.planifierAuto();
    });
  }

  planifierAuto() {
    clearTimeout(this.minuteurAuto);
    if (!this.auto || !this.visible || this.survol || this.focusDedans) return;
    const delai = Math.max(parseInt(this.dataset.autoDelai, 10) || 7000, (this.pause || 0) - Date.now());
    this.minuteurAuto = setTimeout(() => {
      this.appliquer(this.actif + 1);
      this.planifierAuto();
    }, delai);
  }

  /* — Éditeur de thème — */

  brancherEditeur() {
    if (!this.design) return;
    const choisir = (e) => {
      if (!this.contains(e.target)) return;
      const onglet = e.target.closest('[data-mcol-onglet]');
      if (onglet) {
        this.appliquer(this.onglets.indexOf(onglet), { immediat: true });
        return;
      }
      const carte = e.target.closest('[data-mcol-carte]');
      const panneau = carte && carte.closest('[data-mcol-panneau]');
      if (!panneau) return;
      this.appliquer(this.panneaux.indexOf(panneau), { immediat: true });
      const rail = carte.closest('[data-mcol-rail]');
      if (rail) rail.scrollTo({ left: Math.max(0, carte.offsetLeft - 8), behavior: 'auto' });
    };
    document.addEventListener('shopify:block:select', choisir);
    this.nettoyeurs.push(() => document.removeEventListener('shopify:block:select', choisir));
  }
}

if (!customElements.get('menara-collections')) {
  customElements.define('menara-collections', MenaraCollections);
}
